/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `ContactsData` for the Caintra Connect frontend.
 * @module components/CompanyProfileSections/SecondTab/ContactsData
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import React from "react";
import "../index.css";
import { useTranslation } from "react-i18next";
import { getCompanyFieldValue } from "../../../utils/common";

const ContactsData = ({ company, selectedProfile = 0 }) => {
  const { t } = useTranslation();

  const contacts = getCompanyFieldValue(
    company,
    "contactos",
    selectedProfile,
    [],
  );

  return (
    <div className="profileSection">
      <h2 className="sectionTitle">{t("pages.companyProfile.contactsData")}</h2>
      <div className="gridContainer">
        {contacts &&
          contacts.map((contact, index) => {
            const fullName = `${contact.nombre || ""} ${
              contact.segundoNombre || ""
            } ${contact.apellidoPaterno || ""} ${
              contact.apellidoMaterno || ""
            }`.trim();
            const contactTitle =
              index === 0
                ? t("pages.companyProfile.principalContact")
                : `${t("pages.companyProfile.additionalContact")} ${index}`;

            const showPhone = contact?.mostrarTelefono || false;
            const showCellphone = contact?.mostrarCelular || false;
            const showExtension = contact?.mostrarExtension || false;
            const showEmail = contact?.mostrarCorreoElectronico || false;

            return (
              <div key={index} className="contactSection">
                <h3 className="contactTitle">{contactTitle}</h3>
                <p className="contactName">{fullName}</p>
                <div className="gridRow">
                  {showPhone && (
                    <div className="gridItem">
                      <span className="gridLabel">
                        {t("pages.companyProfile.phone")}
                      </span>
                      <span className="gridValue">
                        {contact.telefono || ""}
                      </span>
                    </div>
                  )}
                  {showExtension && (
                    <div className="gridItem">
                      <span className="gridLabel">
                        {t("pages.companyProfile.extension")}
                      </span>
                      <span className="gridValue">
                        {contact.extension || ""}
                      </span>
                    </div>
                  )}
                  {showCellphone && (
                    <div className="gridItem">
                      <span className="gridLabel">
                        {t("pages.companyProfile.cellphone")}
                      </span>
                      <span className="gridValue">{contact.celular || ""}</span>
                    </div>
                  )}
                  {showEmail && (
                    <div className="gridItem">
                      <span className="gridLabel">
                        {t("pages.companyProfile.emailAddress")}
                      </span>
                      <span className="gridValue completeEmail">
                        {contact.correoElectronico || ""}
                      </span>
                    </div>
                  )}
                </div>
                {index < contacts.length - 1 && (
                  <hr className="contactDivider" />
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default ContactsData;
